import createClient from "openapi-fetch";
import type { paths } from "./generated";
import type { JsonBody } from "./request-body";

/**
 * The clients of the engine's planes other than the keyed partner plane
 * (`createTicketingClient`): each holds its own credential, or none, and no
 * organizer header of its own.
 */

/** Browser-safe door-pass client. It carries only the pass bearer and never
 * invents an organizer header, allowing a one-event pass to derive its tenant
 * at authentication time. */
export function createDoorPassClient(options: {
  baseUrl: string;
  token: string;
  fetch?: (request: Request) => Promise<Response>;
}) {
  const client = createClient<paths>({
    baseUrl: options.baseUrl,
    fetch: options.fetch,
    headers: { Authorization: `Bearer ${options.token}` },
  });
  return {
    scan: (body: JsonBody<paths["/v1/checkins"]["post"]>) =>
      client.POST("/v1/checkins", { body }),
    sync: (body: JsonBody<paths["/v1/checkins/sync"]["post"]>) =>
      client.POST("/v1/checkins/sync", { body }),
    void: (id: string) =>
      client.POST("/v1/checkins/{id}/void", { params: { path: { id } } }),
    manifest: (eventId: string, etag?: string) =>
      client.GET("/v1/events/{id}/manifest", { params: { path: { id: eventId }, header: etag ? { "If-None-Match": etag } : undefined } }),
  };
}

/**
 * The buyer plane (ADR-0014): the same engine, authorized by a session's own
 * `client_secret`. Safe to run in a browser — the secret is the only
 * credential it holds, and it dies with the session.
 */
export function createBuyerClient(options: {
  baseUrl: string;
  clientSecret: string;
  fetch?: (request: Request) => Promise<Response>;
}) {
  const client = createClient<paths>({
    baseUrl: options.baseUrl,
    fetch: options.fetch,
    headers: { Authorization: `Bearer ${options.clientSecret}` },
  });
  return {
    get: (id: string) =>
      client.GET("/v1/buyer/checkout_sessions/{id}", { params: { path: { id } } }),
    update: (
      id: string,
      body: JsonBody<paths["/v1/buyer/checkout_sessions/{id}"]["patch"]>,
    ) =>
      client.PATCH("/v1/buyer/checkout_sessions/{id}", {
        params: { path: { id } },
        body,
      }),
    /** Issue a zero-total session; retries return its existing order within the read window. */
    complete: (id: string) =>
      client.POST("/v1/buyer/checkout_sessions/{id}/complete", { params: { path: { id } } }),
    refreshQuote: (id: string) =>
      client.POST("/v1/buyer/checkout_sessions/{id}/quote", {
        params: { path: { id } },
      }),
    paymentCredentials: (id: string) =>
      client.GET("/v1/buyer/checkout_sessions/{id}/payment_credentials", {
        params: { path: { id } },
      }),
    /**
     * Settle now: the payment was just confirmed in this browser. Answers the
     * buyer session exactly as `get` does — `completed` with its `order_id`
     * once issued. Safe to repeat; nothing in the request is trusted.
     */
    finalize: (id: string) =>
      client.POST("/v1/buyer/checkout_sessions/{id}/finalize", {
        params: { path: { id } },
      }),
    /**
     * Pay with Lissen (LIS-685). Four calls, in the order the drop-in checkout
     * component makes them.
     *
     * `identity` is the only one that changes the cart: it sets the session's
     * subject to the attested buyer and re-runs the quote, so member pricing
     * lands before anything is paid. The other three read or point the
     * payment; none of them is an authorization to buy.
     */
    identity: (
      id: string,
      body: JsonBody<
        paths["/v1/buyer/checkout_sessions/{id}/identity"]["post"]
      >,
    ) =>
      client.POST("/v1/buyer/checkout_sessions/{id}/identity", {
        params: { path: { id } },
        body,
      }),
    /** Withdraw the attestation: the buyer took the chip off or signed out. */
    clearIdentity: (id: string) =>
      client.DELETE("/v1/buyer/checkout_sessions/{id}/identity", {
        params: { path: { id } },
      }),
    enrolment: (
      id: string,
      body: JsonBody<
        paths["/v1/buyer/checkout_sessions/{id}/enrolment"]["post"]
      >,
    ) =>
      client.POST("/v1/buyer/checkout_sessions/{id}/enrolment", {
        params: { path: { id } },
        body,
      }),
    walletSetup: (id: string) =>
      client.POST("/v1/buyer/checkout_sessions/{id}/wallet/setup", {
        params: { path: { id } },
      }),
    walletPay: (
      id: string,
      body: JsonBody<
        paths["/v1/buyer/checkout_sessions/{id}/wallet/pay"]["post"]
      >,
    ) =>
      client.POST("/v1/buyer/checkout_sessions/{id}/wallet/pay", {
        params: { path: { id } },
        body,
      }),
  };
}

/**
 * The public client (LIS-685): the operations that carry NO credential at all.
 *
 * One today — the origins allowed to embed an organizer's checkout component,
 * which the component's own page must read before it can declare its
 * `frame-ancestors`, and therefore before any session or key exists. It is a
 * separate factory rather than a method on the others precisely so a caller
 * cannot pick it up by accident while holding a credential and conclude the
 * answer was scoped to them.
 */
export function createPublicClient(options: {
  baseUrl: string;
  fetch?: (request: Request) => Promise<Response>;
}) {
  const client = createClient<paths>({
    baseUrl: options.baseUrl,
    fetch: options.fetch,
  });
  return {
    embedOrigins: (slug: string) =>
      client.GET("/v1/public/organizers/{slug}/embed", {
        params: { path: { slug } },
      }),
  };
}
